/**
 * Profile Settings Page
 *
 * Manage user profile settings.
 *
 * @module pages/settings/ProfilePage
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { db, functions } from '@/lib/firebase';
import { Card, CardHeader, Button, Input, Badge, Modal, Alert, useToast } from '@/components/ui';
import { User, Save, AlertTriangle, Key } from 'lucide-react';

// =============================================================================
// Component
// =============================================================================

export function ProfilePage() {
  usePageTitle('Profile Settings');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, firebaseUser } = useAuth();
  const toast = useToast();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [saving, setSaving] = useState(false);

  // Password reset success alert state
  const [showPasswordResetSuccess, setShowPasswordResetSuccess] = useState(false);
  const hasCheckedPasswordResetState = useRef(false);

  // Check if redirected from password reset
  useEffect(() => {
    const state = location.state as { passwordReset?: boolean } | null;
    if (state?.passwordReset && !hasCheckedPasswordResetState.current) {
      hasCheckedPasswordResetState.current = true;
      setShowPasswordResetSuccess(true);
      // Clear the state so the alert doesn't show again on refresh
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state, location.pathname]);

  // Change password state
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordEmailSent, setPasswordEmailSent] = useState(false);

  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [deleting, setDeleting] = useState(false);

  const hasChanges =
    firstName !== (user?.firstName || '') ||
    lastName !== (user?.lastName || '');

  const handleChangePassword = async () => {
    if (!user?.email) return;

    setChangingPassword(true);
    try {
      const requestPasswordReset = httpsCallable<{ email: string }, { success: boolean }>(
        functions,
        'requestPasswordReset'
      );
      await requestPasswordReset({ email: user.email });
      setPasswordEmailSent(true);
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      toast.error('Failed to send password reset email. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSave = async () => {
    if (!user || !firebaseUser) return;

    setSaving(true);
    try {
      // Combine firstName and lastName for displayName
      const combinedDisplayName = [firstName.trim(), lastName.trim()]
        .filter(Boolean)
        .join(' ') || null;

      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.id), {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        displayName: combinedDisplayName,
        updatedAt: serverTimestamp(),
      });

      // Also update Firebase Auth profile for consistency
      await updateProfile(firebaseUser, {
        displayName: combinedDisplayName,
      });

      // Update member documents in all organizations
      const memberUpdates = user.orgMemberships?.map((membership) =>
        updateDoc(doc(db, 'organizations', membership.orgId, 'members', user.id), {
          displayName: combinedDisplayName,
        })
      ) || [];
      await Promise.all(memberUpdates);

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const deleteAcc = httpsCallable(functions, 'deleteAccount');
      await deleteAcc();

      toast.success('Account deleted successfully');
      setShowDeleteModal(false);

      // Navigate to login page
      navigate('/login');
    } catch (error: unknown) {
      console.error('Failed to delete account:', error);
      const err = error as { message?: string };
      toast.error(err.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Profile Details */}
      <Card>
        <CardHeader
          title="Profile Details"
        />

        <div className="space-y-6">
          {/* Password Reset Success Alert */}
          {showPasswordResetSuccess && (
            <Alert
              variant="success"
              onDismiss={() => setShowPasswordResetSuccess(false)}
            >
              Your password has been updated successfully.
            </Alert>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
              />
              <Input
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
              />
            </div>

            <Input
              label="Email"
              value={user?.email || ''}
              disabled
              hint="Your email address cannot be changed."
            />

            {/* Change Password */}
            <div className="pt-2">
              <p className="text-sm font-medium text-gray-700 mb-1.5">Password</p>
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleChangePassword}
                  loading={changingPassword}
                  disabled={passwordEmailSent}
                  leftIcon={<Key className="h-4 w-4" />}
                >
                  {passwordEmailSent ? 'Email Sent' : 'Change Password'}
                </Button>
                {passwordEmailSent && (
                  <p className="text-sm text-gray-500">
                    Check your email for the password reset link.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Card>

      {/* Connected Accounts */}
      <Card>
        <CardHeader
          title="Connected Accounts"
        />

        <div className="space-y-3">
          {/* Email/Password */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-gray-200">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Email & Password</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
            {user?.providers?.includes('password') && (
              <Badge variant="success">Connected</Badge>
            )}
          </div>

          {/* Google */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-gray-200">
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Google</p>
                <p className="text-sm text-gray-500">Sign in with Google</p>
              </div>
            </div>
            {user?.providers?.includes('google.com') ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Button variant="secondary" size="sm">Connect</Button>
            )}
          </div>

          {/* GitHub */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-gray-200">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">GitHub</p>
                <p className="text-sm text-gray-500">Sign in with GitHub</p>
              </div>
            </div>
            {user?.providers?.includes('github.com') ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Button variant="secondary" size="sm">Connect</Button>
            )}
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-error-200">
        <CardHeader
          title="Danger Zone"
        />

        <div className="flex items-center justify-between p-4 bg-error-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Delete Account</p>
            <p className="text-sm text-gray-500">
              Permanently delete your account and all associated data.
            </p>
          </div>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            Delete Account
          </Button>
        </div>
      </Card>

      {/* Delete Account Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConfirmDelete('');
        }}
        title="Delete Account"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmDelete('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAccount}
              loading={deleting}
              disabled={confirmDelete !== 'DELETE'}
            >
              Delete My Account
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-error-50 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-error-800">
              <p className="font-medium">This action cannot be undone.</p>
              <p className="mt-1">
                Your account will be permanently deleted. You will be removed from
                all organizations you are a member of.
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            <strong>Note:</strong> If you are the sole owner of any organization,
            you must transfer ownership or delete those organizations first.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm
            </label>
            <Input
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder="DELETE"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}